import React from "react";
import DOMPurify from "dompurify";
import { hydrate } from "react-dom";
import { loadableReady } from "@loadable/component";
import { BrowserRouter } from "react-router-dom";
import { ConfigProvider } from "antd";
import { ApolloProvider } from "@apollo/client";
import { AuthProvider, AuthContextConsumer } from "./components/auth";

// Apollo
import { getApolloClient } from "./graphql/client/apollo-client";

// CSS
import "antd/lib/date-picker/style/index";
import "./variables.css";
import "./client.css";
import "./global.css";

import { TrustedTypesWindow } from "trusted-types/lib";
import { Auth0ContextInterface, User } from "@auth0/auth0-react";
import { NotificationSubscriptionProvider } from "./components/subscriptions/notification-subscription-provider";
import { createNetworkStatusNotifier } from "react-apollo-network-status";
import { CrudUserFeedbackContainer } from "./containers/crud-user-feedback";
import { GlobalContextProvider } from "./context/global-context";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TaxonomyProvider } from "./context/taxonomy-context";

const trustedTypes = (window as TrustedTypesWindow)?.trustedTypes;
if (trustedTypes && trustedTypes.createPolicy) {
  trustedTypes.createPolicy("default", {
    createHTML: (input) => DOMPurify.sanitize(input, { RETURN_DOM_FRAGMENT: false }),
    createScriptURL: (input) =>
      input === "https://www.google-analytics.com/analytics.js" ? input : "",
  });
}

const { link: apolloLinkNetworkStatus, useApolloNetworkStatusReducer } =
  createNetworkStatusNotifier();

const isProd = process.env.NODE_ENV === "production";
const App = !isProd ? require("./containers/hot.app").default : require("./containers/app").default;
const root = document.getElementById("root");

const queryClient = new QueryClient();

loadableReady(() => {
  hydrate(
    <BrowserRouter>
      <AuthProvider>
        <AuthContextConsumer>
          {(auth0Ctx: Auth0ContextInterface<User>) => (
            <ApolloProvider client={getApolloClient(apolloLinkNetworkStatus, auth0Ctx)}>
              <NotificationSubscriptionProvider>
                <ConfigProvider input={{ autoComplete: "off" }}>
                  <QueryClientProvider client={queryClient}>
                    <CrudUserFeedbackContainer
                      useApolloNetworkStatusReducer={useApolloNetworkStatusReducer}
                    />
                    <GlobalContextProvider>
                      <TaxonomyProvider>
                        <App />
                      </TaxonomyProvider>
                    </GlobalContextProvider>
                  </QueryClientProvider>
                </ConfigProvider>
              </NotificationSubscriptionProvider>
            </ApolloProvider>
          )}
        </AuthContextConsumer>
      </AuthProvider>
    </BrowserRouter>,
    root
  );
});
